/**
 * Custom Canvas Controls
 * Provides auto-layout control for the workflow canvas
 * Styled to match React Flow's standard Controls component
 */

import React from 'react';
import { useReactFlow, ControlButton } from 'reactflow';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { autoLayoutNodes } from '@flow-studio/services';
import { fromReactFlowNodes } from '@flow-studio/services';
import { AutoLayoutIcon, SmartEdgesIcon, LockIcon, HandIcon } from '@bizfirst/common-react';

interface CanvasControlsProps {
  handMode?: boolean;
  onToggleHand?: () => void;
}

export const CanvasControls: React.FC<CanvasControlsProps> = ({ handMode, onToggleHand }) => {
  const { fitView, getNodes } = useReactFlow();
  const { setNodes, edges, updateAllEdgeTypes } = useWorkflowStore();
  const { theme: _theme, useSmartEdges, toggleSmartEdges, isCanvasLocked, toggleCanvasLock } = useUIStore() as any;

  const handleAutoLayout = () => {
    // Get current nodes from React Flow
    const currentNodes = getNodes();

    // Convert to our internal format
    const internalNodes = fromReactFlowNodes(currentNodes);

    // Apply auto-layout
    const layoutedNodes = autoLayoutNodes(internalNodes, edges);

    // Update the store (which will sync to React Flow)
    setNodes(layoutedNodes);

    // Fit view after layout
    setTimeout(() => {
      fitView({ padding: 0.2, duration: 500 });
    }, 100);
  };

  const handleToggleSmartEdges = () => {
    // Toggle the setting
    toggleSmartEdges();

    // Update all existing edges to use the new type
    const newEdgeType = !useSmartEdges ? 'smart' : 'smoothstep';
    updateAllEdgeTypes(newEdgeType);
  };

  return (
    <>
      <ControlButton
        onClick={onToggleHand}
        title={handMode ? 'Hand tool ON - drag moves the whole workflow (click to turn off, or hold Space)' : 'Hand tool - drag to move the whole workflow (or hold Space)'}
        className={handMode ? 'react-flow-control-button--hand-active' : ''}
        aria-pressed={!!handMode}
        aria-label="Hand tool"
      >
        <HandIcon size={16} />
      </ControlButton>

      <ControlButton onClick={handleAutoLayout} title="Auto Layout">
        <AutoLayoutIcon size={16} />
      </ControlButton>

      <ControlButton
        onClick={handleToggleSmartEdges}
        title={useSmartEdges ? "Disable Smart Edges (edges avoid nodes)" : "Enable Smart Edges (edges avoid nodes)"}
        className={useSmartEdges ? 'react-flow-control-button--smart-edges-active' : ''}
      >
        <SmartEdgesIcon size={16} smart={useSmartEdges} />
      </ControlButton>

      <ControlButton
        onClick={toggleCanvasLock}
        title={isCanvasLocked ? "Unlock Canvas" : "Lock Canvas (prevent edits)"}
        className={isCanvasLocked ? 'react-flow-control-button--lock-active' : ''}
        style={isCanvasLocked ? { color: '#ef4444' } : {}}
      >
        <LockIcon size={16} />
      </ControlButton>
    </>
  );
};
